'use client';

import { useCallback, useEffect, useState } from 'react';
import { ServiceSignature } from '../core/_models';
import BaseModal from './_BaseModal';
import useAccountChannel from '../hooks/useAccountChannel';
import { APIError, useAxios } from '../core/_axios';
import {
    updateSignatureTranscriptionByUniqueIdentifier,
    syncSignatureWithBridgeByUniqueIdentifier,
    removeSignatureByUniqueIdentifier,
    toggleSignatureLeaveOutOfPrintedMaterialsByUniqueIdentifier,
} from '../core/_requests';
import useLocalizedCopy from '../hooks/useLocalizedCopy';

function ModalViewSignatureForm({
    signature,
}: {
    signature: ServiceSignature,
}) {
    const { copy } = useLocalizedCopy();
    const [manually_inputted_personName, setManuallyInputtedPersonName] = useState<string>(
        signature.public_data.latest_signature_transcription?.generated_json?.name || ''
    );
    const [manually_inputted_phoneNumber, setManuallyInputtedPhoneNumber] = useState<string>(
        signature.public_data.latest_signature_transcription?.generated_json?.phone_number || ''
    );
    const [manually_inputted_emailAddress, setManuallyInputtedEmailAddress] = useState<string>(
        signature.public_data.latest_signature_transcription?.generated_json?.email || ''
    );
    const [manually_inputted_addressLine1, setManuallyInputtedAddressLine1] = useState<string>(
        signature.public_data.latest_signature_transcription?.generated_json?.mailing_address_line_1 || ''
    );
    const [manually_inputted_addressLine2, setManuallyInputtedAddressLine2] = useState<string>(
        signature.public_data.latest_signature_transcription?.generated_json?.mailing_address_line_2 || ''
    );

    useAccountChannel({
        onSignatureUpdated: (reasonForUpdate, updatedSignature) => {
            if (updatedSignature.unique_identifier === signature.unique_identifier) {
                setManuallyInputtedPersonName(
                    updatedSignature.public_data.latest_signature_transcription?.generated_json?.name || ''
                );
                setManuallyInputtedPhoneNumber(
                    updatedSignature.public_data.latest_signature_transcription?.generated_json?.phone_number || ''
                );
                setManuallyInputtedEmailAddress(
                    updatedSignature.public_data.latest_signature_transcription?.generated_json?.email || ''
                );
                setManuallyInputtedAddressLine1(
                    updatedSignature.public_data.latest_signature_transcription?.generated_json?.mailing_address_line_1 || ''
                );
                setManuallyInputtedAddressLine2(
                    updatedSignature.public_data.latest_signature_transcription?.generated_json?.mailing_address_line_2 || ''
                );
            }
        },
    });

    useEffect(() => {
        setManuallyInputtedPersonName(signature.public_data.latest_signature_transcription?.generated_json?.name || '');
        setManuallyInputtedPhoneNumber(signature.public_data.latest_signature_transcription?.generated_json?.phone_number || '');
        setManuallyInputtedEmailAddress(signature.public_data.latest_signature_transcription?.generated_json?.email || '');
        setManuallyInputtedAddressLine1(signature.public_data.latest_signature_transcription?.generated_json?.mailing_address_line_1 || '');
        setManuallyInputtedAddressLine2(signature.public_data.latest_signature_transcription?.generated_json?.mailing_address_line_2 || '');
    }, [signature]);

    return (
        <div className="flex">
            <div className="flex w-1/2 h-100 items-center justify-center">
                <img
                    style={{ maxHeight: 'calc(100vh - 300px)' }}
                    src={signature.public_data.screen_capture_url}
                    alt="Signature"
                />
            </div>
            <div className="flex w-1/2 h-100 items-center justify-center">
                <div className="max-w-[400px] flex flex-col gap-4 w-full">
                    <div>
                        <label
                            htmlFor="manually_inputted_person_name"
                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                            {`Person's Name`}
                        </label>
                        <input
                            id="manually_inputted_person_name"
                            name="manually_inputted_person_name"
                            type="text"
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            placeholder="John Doe"
                            required={false}
                            autoComplete="off"
                            autoFocus={false}
                            value={manually_inputted_personName}
                            onChange={(e) => setManuallyInputtedPersonName(e.target.value)}
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="manually_inputted_phone_number"
                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                            Phone Number
                        </label>
                        <input
                            id="manually_inputted_phone_number"
                            name="manually_inputted_phone_number"
                            type="text"
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            placeholder="(555) 555-5555"
                            required={false}
                            autoComplete="new-password"
                            autoFocus={false}
                            value={manually_inputted_phoneNumber}
                            onChange={(e) => setManuallyInputtedPhoneNumber(e.target.value)}
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="manually_inputted_email_address"
                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                            {copy.Common.labels.email}
                        </label>
                        <input
                            id="manually_inputted_email_address"
                            name="manually_inputted_email_address"
                            type="email"
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            placeholder="example@example.com"
                            required={false}
                            autoComplete="new-password"
                            autoFocus={false}
                            value={manually_inputted_emailAddress}
                            onChange={(e) => setManuallyInputtedEmailAddress(e.target.value)}
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="manually_inputted_address_line_1"
                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                            Address Line 1
                        </label>
                        <input
                            id="manually_inputted_address_line_1"
                            name="manually_inputted_address_line_1"
                            type="text"
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            placeholder="123 Main St"
                            required={false}
                            autoComplete="new-password"
                            autoFocus={false}
                            value={manually_inputted_addressLine1}
                            onChange={(e) => setManuallyInputtedAddressLine1(e.target.value)}
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="manually_inputted_address_line_2"
                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                            Address Line 2
                        </label>
                        <input
                            id="manually_inputted_address_line_2"
                            name="manually_inputted_address_line_2"
                            type="text"
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            placeholder="Apt 456"
                            required={false}
                            autoComplete="new-password"
                            autoFocus={false}
                            value={manually_inputted_addressLine2}
                            onChange={(e) => setManuallyInputtedAddressLine2(e.target.value)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function ModalViewSignature({
    signature,
    isOpen,
    onCloseModal,
}: {
    signature?: ServiceSignature;
    isOpen: boolean;
    onCloseModal: () => void;
}) {
    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    const [saving, setSaving] = useState(false);
    const [errorSaving, setErrorSaving] = useState<string | null>(null);

    const [confirmingRemove, setConfirmingRemove] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [errorRemoving, setErrorRemoving] = useState<string | null>(null);

    const [leaveOutOfPdfBooks, setLeaveOutOfPdfBooks] = useState(
        signature?.leave_signature_out_of_printed_pdf_and_books || false
    );

    const onFormSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (saving) return;

            setErrorSaving(null);
            setSaving(true);

            const formData = new FormData(event.currentTarget);
            const data = Object.fromEntries(formData.entries());

            if (!signature?.public_data.based_on_simple_signature) {
                try {
                    await updateSignatureTranscriptionByUniqueIdentifier(axios, signature.unique_identifier, {
                        transcription: { ...data },
                    });
                    onCloseModal();
                } catch (exception) {
                    console.error(exception);
                    const errorMessage =
                        exception instanceof APIError
                            ? exception.message
                            : copy.Errors.generic;
                    setErrorSaving(errorMessage);
                    setSaving(false);
                }
            } else {
                try {
                    await syncSignatureWithBridgeByUniqueIdentifier(axios, signature.unique_identifier);
                    onCloseModal();
                } catch (exception) {
                    console.error(exception);
                    const errorMessage =
                        exception instanceof APIError
                            ? exception.message
                            : copy.Errors.generic;
                    setErrorSaving(errorMessage);
                    setSaving(false);
                }
            }
        },
        [axios, saving, onCloseModal, signature]
    );

    const onRemoveSignature = useCallback(async () => {
        if (removing) {
            return;
        }
        setErrorRemoving(null);
        setRemoving(true);
        try {
            await removeSignatureByUniqueIdentifier(axios, signature.unique_identifier)
            onCloseModal();
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorRemoving(errorMessage)
            setRemoving(false)
        }
    }, [axios, removing, onCloseModal, signature?.unique_identifier]);

    const handleTopRightActionChange = useCallback(
        async (leaveOutPdfBooks: boolean) => {
            try {
                setLeaveOutOfPdfBooks(leaveOutPdfBooks);
                await toggleSignatureLeaveOutOfPrintedMaterialsByUniqueIdentifier(axios, signature?.unique_identifier!, leaveOutPdfBooks);
            } catch (exception) {
                console.error(exception);
            }
        },
        [axios, signature]
    );

    useEffect(() => {
        if (!isOpen) {
            setSaving(false);
            setErrorSaving(null);
            setConfirmingRemove(false);
            setRemoving(false);
            setErrorRemoving(null);
        }
    }, [isOpen])

    useAccountChannel({
        onSignatureUpdated: (reasonForUpdate, updatedSignature) => {
            if (updatedSignature.unique_identifier === signature?.unique_identifier) {
                setLeaveOutOfPdfBooks(updatedSignature.leave_signature_out_of_printed_pdf_and_books || false);
            }
        },
    });

    return <BaseModal
        isFullScreen={signature?.public_data.based_on_simple_signature ? false : true}
        isOpen={isOpen}
        setIsOpen={onCloseModal}
        title={!signature ? `` : `${signature.public_data.based_on_simple_signature ? 'View QR Code Signature' : 'View iPad Signature'}`}
        removeAction={!signature ? null : <>
            {!confirmingRemove && <button
                className="btn btn-lg btn-danger"
                type="button"
                onClick={() => {
                    setConfirmingRemove(true)
                }}
            >
                Remove
            </button>}
        </>}

        topRightAction={
            !signature ? null : (
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="leaveOutPdfBooks"
                        className="form-checkbox h-5 w-5 text-primary-600"
                        checked={leaveOutOfPdfBooks}
                        onChange={(e) => handleTopRightActionChange(e.target.checked)}
                    />
                    <label htmlFor="leaveOutPdfBooks" className="text-sm font-medium text-gray-700">
                        Leave this signature out of printed PDF and books?
                    </label>
                </div>
            )
        }
        actions={!signature ? null : <>
            {confirmingRemove ? <>
                <p className="text-sm font-semibold me-3">
                    <p>
                        Are you sure you want to remove this signature?
                    </p>
                    {errorRemoving && <p className="text-red-500">{errorRemoving}</p>}
                </p>
                <button
                    className="btn btn-lg btn-light me-3"
                    type="button"
                    onClick={() => {
                        setConfirmingRemove(false)
                    }}
                    disabled={removing}
                >
                    Cancel
                </button>
                <button
                    className="btn btn-lg btn-danger"
                    type="button"
                    onClick={onRemoveSignature}
                    disabled={removing}
                >
                    {!removing ? `Yes, Remove` : `Removing...`}
                </button>
            </> : <>
                <button
                    className="btn btn-lg btn-light me-3"
                    type="button"
                    onClick={() => {
                        onCloseModal()
                    }}
                >
                    Close
                </button>
                {!signature.public_data.based_on_simple_signature ? <button
                    className="btn btn-lg btn-primary"
                    type="submit"
                >
                    {saving ? 'Confirming...' : 'Confirm Transcription'}
                </button> : <button
                    className="btn btn-lg btn-primary"
                    type="submit"
                >
                    {saving ? 'Syncing...' : 'Sync with Bridge'}
                </button>}
            </>}
        </>}
        onFormSubmit={confirmingRemove ? undefined : onFormSubmit}
        error={confirmingRemove ? null : errorSaving}
    >
        {!signature ? <></> : <>
            {signature.public_data.based_on_simple_signature ? (
                <div className="flex items-center justify-center">
                    <div>
                        <p className="font-bold">
                            Name: {signature.public_data.based_on_simple_signature.full_name || 'N/A'}
                        </p>
                        <p className="font-bold">
                            Phone Number: {signature.public_data.based_on_simple_signature.phone_number || 'N/A'}
                        </p>
                        <p className="font-bold">
                            Email Address: {signature.public_data.based_on_simple_signature.email_address || 'N/A'}
                        </p>
                        <p className="font-bold">
                            Address Line 1: {signature.public_data.based_on_simple_signature.mailing_address_line1 || 'N/A'}
                        </p>
                        <p className="font-bold">
                            Address Line 2: {signature.public_data.based_on_simple_signature.mailing_address_line2 || 'N/A'}
                        </p>
                        <p className="font-bold">
                            Receive Advanced Planning Info: <>
                                {typeof signature.public_data.based_on_simple_signature.interested_in_planning_ahead === 'boolean' ? <>
                                    {signature.public_data.based_on_simple_signature.interested_in_planning_ahead ? 'Yes' : 'No'}
                                </> : <>
                                    N/A
                                </>}
                            </>
                        </p>
                    </div>
                </div>
            ) : (
                <ModalViewSignatureForm signature={signature} />
            )}
        </>}
    </BaseModal>
}